import React, { Component } from 'react';
import './App.css';

interface IProps {}

interface IState {
  answers: string[];
  evaluations: string[];
  guess: string;
  solution: string;
  gameState: GameState;
  maxTries: number;
  copiedToClipboard: boolean;
}

enum GameState {
  InProgress,
  Win,
  Lose,
}

export default class App extends Component<IProps, IState> {
  private numbers: string = '1234567890';
  private guessRef = React.createRef<HTMLDivElement>();

  constructor(props: IProps) {
    super(props);
    this.state = {
      answers: [],
      evaluations: [],
      guess: '',
      solution: '',
      gameState: GameState.InProgress,
      maxTries: 0,
      copiedToClipboard: false,
    };
  }

  componentDidMount() {
    this.restartGame();
  }

  componentWillUnmount() {
    this.endGame();
  }

  restartGame() {
    const solution = `${Math.round(Math.random() * 9999)}`.padStart(4, '0');
    const maxTries = 6;

    this.setState({
      solution,
      answers: [],
      evaluations: [],
      guess: '',
      maxTries,
      gameState: GameState.InProgress,
      copiedToClipboard: false,
    });

    document.addEventListener('keydown', this.handleKeyDown);
  }

  endGame() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  render() {
    const { maxTries, answers } = this.state;
    return (
      <div className="App">
        <header>
          <h1>Numberle</h1>
          <p>
            Guess the combination. {maxTries - answers.length}/{maxTries} tries left.
          </p>
        </header>
        <div id="gameboard">
          {this.renderAnswers()}
          {this.renderGuess()}
          {this.renderRemaining()}
        </div>
        <footer>
          {this.renderKeyboard()}
          <p id="disclaimers">
            A parody of <a href="https://www.powerlanguage.co.uk/wordle/">Wordle</a> by{' '}
            <a href="https://twitter.com/thurinus">@thurinus</a>.
          </p>
          {/* {this.renderDebugging()} */}
        </footer>
        {this.renderEOGDialog()}
      </div>
    );
  }

  renderAnswers() {
    const { answers, evaluations } = this.state;

    return answers.map((answer, index) => {
      const evaluation = evaluations[index];
      return (
        <div key={`r-${index}`} className="game-row" ref={this.guessRef}>
          {[...answer].map((letter, letterIndex) => {
            let modifier = 'none';
            switch (evaluation[letterIndex]) {
              case 'b':
                modifier = 'bull';
                break;
              case 'c':
                modifier = 'cow';
                break;
            }
            return (
              <div key={`l-${letterIndex}`} className={`tile ${modifier}`}>
                {letter}
              </div>
            );
          })}
        </div>
      );
    });
  }

  renderGuess() {
    const { guess, solution, gameState } = this.state;

    if (gameState !== GameState.InProgress) {
      return null;
    }

    const formatted = guess + ' '.repeat(solution.length - guess.length);
    return (
      <div className="game-row">
        {[...formatted].map((letter, letterIndex) => (
          <div key={`l-${letterIndex}`} className="tile">
            {letter}
          </div>
        ))}
      </div>
    );
  }

  renderRemaining() {
    const { answers, maxTries, solution } = this.state;
    const n = Math.max(0, maxTries - answers.length - 1);
    return (
      <>
        {[...Array(n)].map((_, rowIndex) => (
          <div className="game-row" key={`r-${rowIndex}`}>
            {[...Array(solution.length)].map((_, columnIndex) => (
              <div className={`tile`} key={`l-${columnIndex}`}></div>
            ))}
          </div>
        ))}
      </>
    );
  }

  renderKeyboard() {
    return (
      <div id="keyboard">
        {[...this.numbers].map((element) => {
          return (
            <button
              key={`k-${element}`}
              className="keyboard-key"
              onClick={() => {
                this.addDigit(element);
              }}
            >
              {element}
            </button>
          );
        })}
        <button onClick={this.subtractDigit}>⌫</button>
        <button onClick={this.submitAnswer}>⏎</button>
      </div>
    );
  }

  renderEOGDialog() {
    const { gameState, answers, solution, copiedToClipboard } = this.state;

    if (gameState === GameState.InProgress) {
      return null;
    }

    const numGuesses = answers.length;

    let result = `The number was ${solution}. Better luck next time!`;

    if (gameState === GameState.Win) {
      result = `You got the correct combination (${solution}) in ${numGuesses} ${
        numGuesses > 1 ? 'guesses' : 'guess'
      }.`;
    }

    return (
      <div id="overlay">
        <div id="eog-dialog">
          <p>{result}</p>
          <div>
            {gameState === GameState.Win && (
              <button onClick={() => this.copyScoreToClipboard()}>Share</button>
            )}
            <button onClick={() => this.restartGame()}>New Game</button>
            {copiedToClipboard && <div>Copied results to clipboard</div>}
          </div>
        </div>
      </div>
    );
  }

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      this.submitAnswer();
    } else if (e.key === 'Backspace') {
      this.subtractDigit();
    } else if (this.numbers.includes(e.key)) {
      this.addDigit(e.key);
    }
    this.scrollToBottom();
  };

  private addDigit(digit: string) {
    const { guess, solution } = this.state;
    if (guess.length < solution.length) {
      this.setState({
        guess: guess + digit,
      });
    }
  }

  subtractDigit = () => {
    const { guess } = this.state;
    this.setState({
      guess: guess.substring(0, guess.length - 1),
    });
  };

  submitAnswer = () => {
    const { answers, guess, solution, evaluations, maxTries } = this.state;

    if (guess.length < solution.length) {
      return;
    }

    const evaluation = this.evaluateAnswer(guess, solution);

    let gameState = GameState.InProgress;

    if (evaluation.indexOf('c') === -1 && evaluation.indexOf('n') === -1) {
      gameState = GameState.Win;
    } else if (answers.length + 1 >= maxTries) {
      gameState = GameState.Lose;
    }

    if (gameState !== GameState.InProgress) {
      this.endGame();
    }

    this.setState({
      answers: [...answers, guess],
      guess: '',
      evaluations: [...evaluations, evaluation],
      gameState,
    });
  };

  scrollToBottom() {
    this.guessRef.current?.scrollIntoView({ behavior: 'smooth' });
    // requestAnimationFrame(() => {
    //   this.guessRef.current?.scrollIntoView({ behavior: 'smooth' });
    // });
  }

  evaluateAnswer(guess: string, solution: string) {
    // index number of each char
    const manifest = new Map<string, number>();
    [...solution].forEach((value) => {
      const currCount = manifest.get(value) ?? 0;
      manifest.set(value, currCount + 1);
    });

    const evaluation: string[] = new Array(solution.length);
    // search for bulls
    [...guess].forEach((letter, index) => {
      if (letter === solution.charAt(index)) {
        evaluation[index] = 'b';
        const currCount = manifest.get(letter) ?? 0;
        manifest.set(letter, currCount - 1);
      }
    });

    // search for cows and none
    [...guess].forEach((letter, index) => {
      if (letter !== solution.charAt(index)) {
        const currCount = manifest.get(letter) ?? 0;
        if (currCount === 0) {
          evaluation[index] = 'n';
        } else {
          evaluation[index] = 'c';
          manifest.set(letter, currCount - 1);
        }
      }
    });

    return evaluation.join('');
  }

  async copyScoreToClipboard() {
    const { answers, evaluations, maxTries } = this.state;
    let result = `Numberle ${answers.length}/${maxTries}\n\n`;
    evaluations.forEach((evaluation) => {
      const lineOutput = [...evaluation].reduce((prev, curr) => {
        switch (curr) {
          case 'b':
            return prev + '🟩';
          case 'c':
            return prev + '🟨';
          default:
            return prev + '⬛';
        }
      }, '');
      result += `${lineOutput}\n`;
    });

    await navigator.clipboard.writeText(result);

    this.setState({ copiedToClipboard: true });
  }

  renderDebugging() {
    const { evaluations, answers, ...rest } = this.state;
    return <code>State: {JSON.stringify(rest, null, 2)}</code>;
  }
}
